import React, { useEffect, useRef } from 'react';

import { TZDate } from '@date-fns/tz';
import { Heading, Caption, Body } from '@depop/web-thrift-ui/Typography';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { ShopDropCountdownItem } from '../ShopDropCountdownItem';

import styles from './styles.module.css';

import { ButtonExperiment } from '@/components/ButtonExperiment';
import { ExtendedLink } from '@/components/ExtendedLink';
import {
  RQ_FOLLOWING_RELATIONSHIP,
  RQ_SHOP_USERNAME_KEY,
} from '@/modules/ReactQuery/cacheKeys';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { useCountdownInterval } from '@/modules/countdown/hooks/useCountdownInterval';
import { useFollowingRelationship } from '@/modules/follows/useFollowingRelationship';
import { useFollows } from '@/modules/follows/useFollows';
import { useCurrentLocation } from '@/modules/location/useCurrentLocation';
import { reloadPage } from '@/modules/routing/helpers';
import { useExtendedRouter } from '@/modules/routing/useExtendedRouter';
import { formatDropDate } from '@/modules/shop/helpers/dateFormatters';
import {
  getDropHeading,
  getDropCaption,
} from '@/modules/shop/helpers/getDropHeading';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Props = {
  dropDate: string;
  firstName: string;
  timeZoneName: string;
  isPostLaunch?: boolean;
  sellerId: number;
  username: string;
};

export function ShopDropCountdown({
  dropDate,
  firstName,
  timeZoneName,
  isPostLaunch = false,
  sellerId,
  username,
}: Props) {
  const tShop = useTranslations('shop');
  const [isAuth, currentUser] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useExtendedRouter();
  const { location } = useCurrentLocation();

  const dropDateObj = new TZDate(dropDate);
  const { interval, countdownComplete } = useCountdownInterval(dropDateObj);

  const {
    data: followingRelationshipData,
    isLoading: isFollowingRelationshipLoading,
  } = useFollowingRelationship({
    sellerId,
    username,
  });
  const isFollowing = !!followingRelationshipData?.data?.is_following;

  const { isUpdatingFollowCount, handleFollowsButtonClick } = useFollows({
    isFollowing,
    queriesToInvalidate: [
      [RQ_FOLLOWING_RELATIONSHIP, username],
      [RQ_SHOP_USERNAME_KEY, username],
    ],
  });

  useEffect(() => {
    if (countdownComplete) {
      reloadPage();
    }
  }, [countdownComplete]);

  const hasSentCountdownViewEvent = useRef(false);

  useEffect(() => {
    if (hasSentCountdownViewEvent.current || isFollowingRelationshipLoading) {
      return;
    }

    hasSentCountdownViewEvent.current = true;
    sendActivityTrackerEvent(
      ActivityTrackerEventType.SHOP_DROP_COUNTDOWN_VIEW,
      {
        sellerId,
        isFollowing,
        schemaVersion: '1.0',
      }
    );
  }, [
    isFollowingRelationshipLoading,
    isFollowing,
    sellerId,
    sendActivityTrackerEvent,
  ]);

  function handleSignupClick() {
    sendActivityTrackerEvent(ActivityTrackerEventType.CTA_ACTION, {
      type: 'countdown_to_drop_signup_cta',
      schemaVersion: '1.0',
    });
  }

  async function handleRemindMe() {
    if (!isAuth) {
      const currentPath = pathname || `/${username}/`;
      const currentSearchParams = new URLSearchParams(searchParams.toString());
      currentSearchParams.set('remindMe', 'true');
      const redirectUrl = `${currentPath}?${currentSearchParams.toString()}`;
      const redirect = encodeURIComponent(redirectUrl);
      router.push(`/signup/?redirect=${redirect}`);
      return;
    }

    await handleFollowsButtonClick(sellerId);

    sendActivityTrackerEvent(
      ActivityTrackerEventType.SHOP_DROP_REMIND_ME_ACTION,
      {
        sellerId,
        schemaVersion: '1.0',
      }
    );
  }

  const shouldShowRemindMe = !isFollowing;
  const isReminderSet = isFollowing;

  const heading = getDropHeading({
    isReminderSet,
    isAuth,
    isPostLaunch,
    firstName,
    tShop,
  });

  const caption = getDropCaption({
    isReminderSet,
    isAuth,
    isPostLaunch,
    tShop,
  });

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <div className={styles.headingWrapper}>
          <Heading
            variant="sm"
            className={isReminderSet ? styles.headingWithIcon : undefined}
          >
            {heading}
          </Heading>
          <Caption variant="lg-regular" className={styles.caption}>
            {caption}
          </Caption>
          {isAuth && shouldShowRemindMe && !isPostLaunch && (
            <div className={styles.remindMeButtonWrapper}>
              <ButtonExperiment
                onClick={handleRemindMe}
                legacyClassName={styles.remindMeButton}
                disabled={isUpdatingFollowCount}
              >
                {tShop('DropRemindMeButton')}
              </ButtonExperiment>
            </div>
          )}
          {!isAuth && !isPostLaunch && (
            <div className={styles.remindMeButtonWrapper}>
              <ButtonExperiment
                onClick={handleRemindMe}
                legacyClassName={styles.remindMeButton}
              >
                {tShop('DropRemindMeButton')}
              </ButtonExperiment>
            </div>
          )}
          {!isAuth && isPostLaunch && (
            <ExtendedLink
              className={styles.signupButtonWrapper}
              href={`/signup/?redirect=${encodeURIComponent(pathname)}`}
            >
              <ButtonExperiment
                onClick={handleSignupClick}
                legacyClassName={styles.signupButton}
              >
                {tShop('DropCountdownButtonGuest')}
              </ButtonExperiment>
            </ExtendedLink>
          )}
        </div>
        <div className={styles.countdownWrapper}>
          <div className={styles.countdown}>
            <ShopDropCountdownItem
              value={isPostLaunch ? 0 : interval.days || 0}
              text={tShop('DropCountdownItemDays')}
              withDivider
            />
            <ShopDropCountdownItem
              value={isPostLaunch ? 0 : interval.hours || 0}
              text={tShop('DropCountdownItemHours')}
              withDivider
            />
            <ShopDropCountdownItem
              value={isPostLaunch ? 0 : interval.minutes || 0}
              text={tShop('DropCountdownItemMinutes')}
              withDivider
            />
            <ShopDropCountdownItem
              value={isPostLaunch ? 0 : interval.seconds || 0}
              text={tShop('DropCountdownItemSeconds')}
            />
          </div>
          <Body variant="sm-regular" className={styles.dropDateCopy}>
            {formatDropDate(dropDate, timeZoneName, location)}
          </Body>
        </div>
      </div>
    </div>
  );
}
